import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist, jira/import/no-restricted-import
import { GraphQLErrors } from '@jira/business__shared-utils-graphql-errors/src/index.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { fetchFields } from './fetchFields.tsx';
import type { FieldsProjectQueryResponse, Field } from './FieldEdge.types.tsx';
import { PROJECT_QUERY } from './ProjectQuery.tsx';
import { transformData } from './transformData.tsx';

const RESOURCE_TYPE_KEY = 'BUSINESS_FIELDS';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (promise: Promise<any>) => Promise<Field[]>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const handleProjectResponse: Handler = async (promise: Promise<any>) => {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const response = (await promise) as FieldsProjectQueryResponse;

	if (response.data?.jira?.jqlBuilder?.fields?.edges == null) {
		throw new GraphQLErrors(response.errors);
	}

	return transformData(response.data?.jira?.jqlBuilder?.fields?.edges);
};

export const fieldsResource: RouteResource<Field[] | null> = createResource<Field[] | null>({
	type: RESOURCE_TYPE_KEY,
	getKey: (matchedRoute) => {
		const { projectKey, projectId } = getProjectKeyId(matchedRoute.match, matchedRoute.query);
		const projectIdOrKey = projectKey || projectId;
		return `${RESOURCE_TYPE_KEY}_project_${projectIdOrKey}`;
	},
	getData: async ({ match, query }, { tenantContext: { cloudId } }) => {
		const { projectKey, projectId } = getProjectKeyId(match, query);
		const projectIdOrKey = projectKey || projectId;

		return fetchFields(PROJECT_QUERY, handleProjectResponse, {
			cloudId,
			jqlContext: `project = '${projectIdOrKey}'`,
		});
	},
	maxAge: 60 * 60 * 1000, // 1 hour,
});

export const useFieldsResource = (): UseResourceHookResponse<Field[] | null> =>
	useResource<Field[] | null>(fieldsResource);
